import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  buyerService,
  accountService,
} from "../../services/enhancedCrudService";
import Select from "react-select";

const BuyerProfileEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [profileData, setProfileData] = useState({
    firm_name: "",
    gst_number: null,
    owner_name: "",
    office_mobile: null,
    mobile_number: "",
    address: {
      address_line: null,
      road: null,
      city: null,
      district: "",
      district_id: "",
      district_name_display: "",
      state: "",
      state_id: "",
      state_name_display: "",
      pin_code: null,
    },
    profile_image: "",
    is_disabled: false,
    subscription_status: false,
  });

  const [imagePreview, setImagePreview] = useState("");

  const fetchStates = async () => {
    try {
      const response = await accountService.getStates();
      if (response && response.length) {
        const stateOptions = response.map((state) => ({
          value: state.id,
          label: state.name,
        }));
        setStates(stateOptions);
      }
    } catch (error) {
      console.error("Error fetching states:", error);
      toast.error("Failed to fetch states");
    }
  };

  const fetchDistricts = async (stateId) => {
    try {
      if (!stateId) {
        setDistricts([]);
        return;
      }
      const response = await accountService.getDistricts(stateId);
      if (response && response.length) {
        const districtOptions = response.map((district) => ({
          value: district.id,
          label: district.name,
        }));
        setDistricts(districtOptions);
      } else {
        setDistricts([]);
      }
    } catch (error) {
      console.error("Error fetching districts:", error);
      toast.error("Failed to fetch districts");
      setDistricts([]);
    }
  };

  useEffect(() => {
    fetchStates();
    if (id) {
      fetchBuyerProfile();
    }
  }, [id]);

  // Fetch districts when user data is loaded and we have a state_id
  useEffect(() => {
    if (profileData.address.state && states.length > 0) {
      fetchDistricts(profileData.address.state);
    }
  }, [profileData.address.state, states]);

  // Bind dropdown data when states and profile data are loaded
  useEffect(() => {
    if (states.length > 0 && profileData.address.state && profileData.address.state_name_display) {
      bindDropdownData();
    }
  }, [states, profileData.address.state, profileData.address.state_name_display]);

  // Bind district data when districts and profile data are loaded
  useEffect(() => {
    if (districts.length > 0 && profileData.address.district && profileData.address.district_name_display) {
      bindDistrictData();
    }
  }, [districts, profileData.address.district, profileData.address.district_name_display]);

  const fetchBuyerProfile = async () => {
    setLoading(true);
    try {
      const response = await buyerService.getProfileById(id);

      if (response && response.success && response.data) {
        const data = response.data;
        console.log("Received buyer profile data:", data);
        console.log("Address data:", data.address);
        setProfileData({
          firm_name: data.firm_name || "",
          gst_number: data.gst_number || null,
          owner_name: data.owner_name || "",
          office_mobile: data.office_mobile || null,
          mobile_number: data.mobile_number || "",
          address: {
            address_line: data.address?.address_line || null,
            road: data.address?.road || null,
            city: data.address?.city || null,
            district: data.address?.district || "",
            district_id: data.address?.district || "",
            district_name_display: data.address?.district_name_display || "",
            state: data.address?.state || "",
            state_id: data.address?.state || "",
            state_name_display: data.address?.state_name_display || "",
            pin_code: data.address?.pin_code || null,
          },
          profile_image: data.profile_image || "",
          is_disabled: data.is_disabled || false,
          subscription_status: data.subscription_status || false,
        });

        if (data.profile_image) {
          setImagePreview(data.profile_image);
        }
      } else {
        toast.error(response.message || "Failed to fetch buyer profile");
      }
    } catch (error) {
      console.error("Error fetching buyer profile:", error);
      toast.error("Failed to fetch buyer profile");
    } finally {
      setLoading(false);
    }
  };

  const bindDropdownData = () => {
    // Handle state binding using profileData state
    console.log("Binding state data:", {
      state: profileData.address?.state,
      state_name_display: profileData.address?.state_name_display,
      states: states
    });
    
    if (profileData.address?.state && profileData.address?.state_name_display) {
      const stateOption = states.find(
        (option) => option.value === profileData.address.state
      );
      if (stateOption) {
        console.log("Found state option:", stateOption);
        setSelectedState(stateOption);
      } else {
        console.log("Creating state option from display name");
        setSelectedState({
          value: profileData.address.state,
          label: profileData.address.state_name_display,
        });
      }
    } else {
      setSelectedState(null);
    }
  };

  const bindDistrictData = () => {
    // Handle district binding using profileData state
    console.log("Binding district data:", {
      district: profileData.address?.district,
      district_name_display: profileData.address?.district_name_display,
      districts: districts
    });
    
    if (profileData.address?.district && profileData.address?.district_name_display) {
      const districtOption = districts.find(
        (option) => option.value === profileData.address.district
      );
      if (districtOption) {
        console.log("Found district option:", districtOption);
        setSelectedDistrict(districtOption);
      } else {
        console.log("Creating district option from display name");
        setSelectedDistrict({
          value: profileData.address.district,
          label: profileData.address.district_name_display,
        });
      }
    } else {
      setSelectedDistrict(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith("address.")) {
      const addressField = name.split(".")[1];
      setProfileData((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value === "" ? null : value,
        },
      }));
    } else {
      setProfileData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : (value === "" ? null : value),
      }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);

      // Update profile data with the file
      setProfileData((prev) => ({
        ...prev,
        profile_image: file,
      }));
    }
  };

  const handleStateChange = (selectedOption) => {
    setSelectedState(selectedOption);
    setSelectedDistrict(null); // Reset district when state changes
    setDistricts([]); // Clear districts list
    setProfileData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        state: selectedOption ? selectedOption.value : "",
        state_id: selectedOption ? selectedOption.value : "",
        district: "",
        district_id: "",
      },
    }));

    // Fetch districts for the selected state
    if (selectedOption) {
      fetchDistricts(selectedOption.value);
    }
  };

  const handleDistrictChange = (selectedOption) => {
    setSelectedDistrict(selectedOption);
    setProfileData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        district: selectedOption ? selectedOption.value : "",
        district_id: selectedOption ? selectedOption.value : "",
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      // Prepare data for API - handle image separately if it's a file
      let updateData = {
        firm_name: profileData.firm_name,
        gst_number: profileData.gst_number || null,
        owner_name: profileData.owner_name,
        mobile_number: profileData.mobile_number,
        is_disabled: profileData.is_disabled,
        subscription_status: profileData.subscription_status,
        address: {
          address_line: profileData.address.address_line || null,
          road: profileData.address.road || null,
          city: profileData.address.city || null,
          district: profileData.address.district_id,
          state: profileData.address.state_id,
          pin_code: profileData.address.pin_code || null,
        },
      };

      // Only include office_mobile if it has a value
      if (profileData.office_mobile && profileData.office_mobile.trim() !== "") {
        updateData.office_mobile = profileData.office_mobile;
      }

      // Update profile data first
      const response = await buyerService.updateProfile(id, updateData);

      // If there's a new image file, update it separately
      if (profileData.profile_image instanceof File) {
        const imageFormData = new FormData();
        imageFormData.append("profile_image", profileData.profile_image);
        await buyerService.changeProfileImage(imageFormData);
      }

      if (response) {
        toast.success("Buyer profile updated successfully");
        // Navigate back with preserved pagination state
        const returnUrl = new URLSearchParams(location.search).get('returnUrl') || '';
        navigate(`/user-list${returnUrl}`);
      } else {
        toast.error("Failed to update buyer profile");
      }
    } catch (error) {
      console.error("Error updating buyer profile:", error);
      toast.error("Failed to update buyer profile");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Navigate back with preserved pagination state
    const returnUrl = new URLSearchParams(location.search).get('returnUrl') || '';
    navigate(`/user-list${returnUrl}`);
  };

  if (loading && !profileData.firm_name) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="page-header mb-3">
        <div className="row align-items-center">
          <div className="col">
            <h3 className="page-title">Edit Buyer Information</h3>
          </div>
          <div className="col-auto">
            <button className="btn btn-secondary" onClick={handleCancel}>
              <i className="feather-arrow-left"></i> Back to List
            </button>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-header">
          <h4 className="card-title">Buyer Information</h4>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* Firm Name */}
              <div className="col-md-4">
                <div className="form-group">
                  <label>Firm Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="firm_name"
                    value={profileData.firm_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              {/* GST Number */}
              <div className="col-md-4">
                <div className="form-group">
                  <label>GST Number</label>
                  <input
                    type="text"
                    className="form-control"
                    name="gst_number"
                    value={profileData.gst_number || ""}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Owner Name */}
              <div className="col-md-4">
                <div className="form-group">
                  <label>Owner Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="owner_name"
                    value={profileData.owner_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              {/* Office Mobile */}
              <div className="col-md-4">
                <div className="form-group">
                  <label>Office Mobile</label>
                  <input
                    type="tel"
                    className="form-control"
                    name="office_mobile"
                    value={profileData.office_mobile || ""}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label>Mobile Number *</label>
                  <input
                    type="tel"
                    className="form-control"
                    name="mobile_number"
                    value={profileData.mobile_number}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label>Address Line</label>
                  <input
                    type="text"
                    className="form-control"
                    name="address.address_line"
                    value={profileData.address.address_line || ""}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label>Road</label>
                  <input
                    type="text"
                    className="form-control"
                    name="address.road"
                    value={profileData.address.road || ""}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    className="form-control"
                    name="address.city"
                    value={profileData.address.city || ""}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label>State</label>
                  <Select
                    options={states}
                    value={selectedState}
                    onChange={handleStateChange}
                    placeholder="Select State"
                    isClearable
                    isSearchable
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label>District</label>
                  <Select
                    options={districts}
                    value={selectedDistrict}
                    onChange={handleDistrictChange}
                    placeholder="Select District"
                    isClearable
                    isSearchable
                    isDisabled={!selectedState}
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label>Pin Code</label>
                  <input
                    type="text"
                    className="form-control"
                    name="address.pin_code"
                    value={profileData.address.pin_code || ""}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
            {/* Action Buttons */}
            <div className="row mt-4">
              <div className="col-12">
                <div className="d-flex justify-content-end gap-2">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Updating...
                      </>
                    ) : (
                      <>
                        <i className="feather-save"></i> Update Profile
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    <i className="feather-x"></i> Cancel
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BuyerProfileEdit;
